import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { absoluteUrl } from '../seo';
import './Features.css';

const featureSections = [
  {
    id: 'interactive-racecard',
    color: '#7c3aed',
    bgFrom: '#fafafa',
    bgTo: '#f5f3ff',
    titleMain: 'Interactive',
    titleAccent: 'Race Card',
    items: [
      {
        name: 'Race Card',
        description:
          'See the field like never before with access to essential race data on every runner. Form, performance, silks and other critical information all in one view.',
      },
      {
        name: 'Filter Races',
        description:
          'Cut through the noise and seamlessly filter unfavoured runners from your view. Giving you a streamlined look at your preferred horses.',
      },
      {
        name: 'Runner Highlights',
        description:
          "Immediately spot critical factors that can influence a horse's performance. Whether a horse is moving class, its form has been franked and how suitable it is over the course and distance.",
      },
      {
        name: 'HorseStat Rating System',
        description:
          'Each race is simulated across 22 variables, 10,000 times, to produce a predicted finishing position for every horse. Our methodology organically evolves to become increasingly accurate over time.',
      },
    ],
  },
  {
    id: 'form-analysis',
    color: '#059669',
    bgFrom: '#f5f3ff',
    bgTo: '#ecfdf5',
    titleMain: 'Form Analysis',
    titleAccent: 'and Stats',
    reverse: true,
    items: [
      {
        name: 'Form',
        description:
          "Often the past is one of the best predictors of the future, easily see a horse's recent finishing positions.",
      },
      {
        name: 'PRB',
        description:
          'Immediately see and understand PRB across multiple different edges to understand how well horses truly perform.',
      },
      {
        name: 'Performance',
        description:
          "Deep dive into a runner's past performance, understand how its weight, class rating have trended and changed over time, what distance it's run out, how it did and more.",
      },
    ],
  },
  {
    id: 'pace-draw',
    color: '#f59e0b',
    bgFrom: '#ecfdf5',
    bgTo: '#fef3c7',
    titleMain: 'Pace Analysis &',
    titleAccent: 'Draw Bias',
    items: [
      {
        name: 'Pace Map',
        description:
          'See how horses tend to run their races, do they stay in the middle of the pack? Hold back? Lead the charge?',
      },
      {
        name: 'Course Pace Bias',
        description:
          'See which running type horses that win on a course tend to have. Do they stay at the front and win? Does the course favour horses that manage pace better and hold back until the end?',
      },
      {
        name: 'Stall IV',
        description:
          'Understand whether horses that start in a specific stall have a better chance of winning each race.',
      },
    ],
  },
  {
    id: 'trainer-jockey-owner',
    color: '#ec4899',
    bgFrom: '#fef3c7',
    bgTo: '#fdf4ff',
    titleMain: 'Trainer, Jockey',
    titleAccent: 'and Owner Stats',
    reverse: true,
    items: [
      {
        name: 'Trainer Stats',
        description:
          'Learn how well a specific trainer has performed historically, see key stats and statistics across distance, course and form to help you evaluate their performance in a given race.',
      },
      {
        name: 'Jockey Stats',
        description:
          'Immediately see how well a specific jockey has performed over the course and distance unlocking important stats around win %, form and A/E.',
      },
      {
        name: 'Owner Stats',
        description:
          'Go a level deeper and analyse how a specific owner has performed at the course and distance, and understand how well owner/trainer and owner/jockey pairings have performed.',
      },
    ],
  },
  {
    id: 'sire-dam',
    color: '#06b6d4',
    bgFrom: '#fdf4ff',
    bgTo: '#ecfeff',
    titleMain: 'Sire & Dam',
    titleAccent: 'Performance',
    items: [
      {
        name: 'Going',
        description:
          "See how well a runner's Sire and Dam have performed over similar conditions, accessing data on form, win rates and PRB.",
      },
      {
        name: 'Class',
        description: 'Access Sire and Dam performance in the relevant class.',
      },
      {
        name: 'Distance',
        description:
          "Learn how a runner's Sire and Dam have performed running over the same distance historically.",
      },
      {
        name: 'Course',
        description:
          "Understand whether a runner's Sire and Dam have run the course before, and access data on their past performance.",
      },
    ],
  },
];

function tryStartMutedVideo(videoEl) {
  if (!videoEl) return;
  videoEl.muted = true;
  videoEl.defaultMuted = true;
  videoEl.setAttribute('muted', '');
  videoEl.setAttribute('playsinline', '');
  videoEl.play().catch(() => {});
}

function useScrollReveal(delay = 0) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  return ref;
}

function FeatureSection({ section }) {
  const sectionVideoSrc =
    section.id === 'interactive-racecard'
      ? '/videos/hs-features-rc.mp4'
      : section.id === 'form-analysis'
        ? '/videos/hs-features-form.mp4'
        : section.id === 'pace-draw'
          ? '/videos/hs-features-pace.mp4'
          : section.id === 'trainer-jockey-owner'
            ? '/videos/hs-trainer-owner.mp4'
          : section.id === 'sire-dam'
            ? '/videos/hs-features-sire-dam.mp4'
        : null;
  const [openItem, setOpenItem] = useState(section.items[0].name);
  const imgRef = useScrollReveal(0);
  const textRef = useScrollReveal(100);

  const toggle = (name) => {
    setOpenItem((prev) => (prev === name ? null : name));
  };

  return (
    <section
      id={section.id}
      className="fs-section"
      style={{
        background: `linear-gradient(180deg, ${section.bgFrom} 0%, ${section.bgTo} 100%)`,
      }}
    >
      <div className="fs-container">
        <div className={`fs-grid${section.reverse ? ' fs-grid--rev' : ''}`}>
          {/* Media */}
          <div
            ref={imgRef}
            className="fs-media"
            style={{
              opacity: 0,
              transform: 'translateY(30px)',
              transition: 'opacity 0.6s ease, transform 0.6s ease',
            }}
          >
            <div className="fs-sticky">
              {sectionVideoSrc ? (
                <video
                  className="fs-feature-video"
                  src={sectionVideoSrc}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  aria-label={`${section.titleMain} ${section.titleAccent} preview`}
                  onLoadedData={(e) => {
                    tryStartMutedVideo(e.currentTarget);
                  }}
                  onCanPlay={(e) => {
                    tryStartMutedVideo(e.currentTarget);
                  }}
                />
              ) : (
                <div
                  className="fs-frame"
                  style={{
                    background: `linear-gradient(135deg, ${section.color}60, ${section.color}20)`,
                  }}
                >
                  <div
                    className="fs-frame-inner"
                    style={{
                      background: `linear-gradient(135deg, ${section.color}08, ${section.color}04)`,
                    }}
                  >
                    <div
                      className="fs-placeholder"
                      style={{
                        background: `linear-gradient(135deg, ${section.color}18, ${section.color}06)`,
                      }}
                    >
                      <svg
                        width="64"
                        height="64"
                        viewBox="0 0 64 64"
                        fill="none"
                        style={{ opacity: 0.25 }}
                      >
                        <rect
                          x="8"
                          y="8"
                          width="48"
                          height="48"
                          rx="8"
                          stroke={section.color}
                          strokeWidth="2"
                        />
                        <rect
                          x="16"
                          y="20"
                          width="32"
                          height="4"
                          rx="2"
                          fill={section.color}
                        />
                        <rect
                          x="16"
                          y="30"
                          width="24"
                          height="4"
                          rx="2"
                          fill={section.color}
                        />
                        <rect
                          x="16"
                          y="40"
                          width="28"
                          height="4"
                          rx="2"
                          fill={section.color}
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Text */}
          <div
            ref={textRef}
            className="fs-text"
            style={{
              opacity: 0,
              transform: 'translateY(30px)',
              transition: 'opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s',
            }}
          >
            <div className="fs-text-inner">
              <div
                className="fs-bar"
                style={{ backgroundColor: section.color }}
              />
              <div className="fs-content">
                <h2 className="fs-h2">
                  {section.titleMain}{' '}
                  <span style={{ color: section.color }}>
                    {section.titleAccent}
                  </span>
                </h2>

                <div className="fs-accordion">
                  {section.items.map((item) => {
                    const isOpen = openItem === item.name;
                    return (
                      <div
                        key={item.name}
                        className="fs-acc-item"
                        style={{
                          background: isOpen
                            ? 'rgba(255,255,255,0.9)'
                            : 'rgba(255,255,255,0.6)',
                          boxShadow: isOpen
                            ? '0 4px 20px rgba(0,0,0,0.08)'
                            : 'none',
                        }}
                        onClick={() => toggle(item.name)}
                      >
                        <button className="fs-acc-btn">
                          <span className="fs-acc-label">{item.name}</span>
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="fs-acc-chevron"
                            style={{
                              color: section.color,
                              transform: isOpen
                                ? 'rotate(180deg)'
                                : 'rotate(0deg)',
                            }}
                          >
                            <path d="m6 9 6 6 6-6" />
                          </svg>
                        </button>
                        <div
                          className="fs-acc-body"
                          style={{ maxHeight: isOpen ? '240px' : '0' }}
                        >
                          <p className="fs-acc-text">{item.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Features() {
  useEffect(() => {
    const retryAutoplay = () => {
      const videos = document.querySelectorAll('.fs-feature-video');
      videos.forEach((videoEl) => {
        tryStartMutedVideo(videoEl);
      });
    };

    retryAutoplay();
    const rafId = window.requestAnimationFrame(retryAutoplay);
    const timeoutId = window.setTimeout(retryAutoplay, 250);

    window.addEventListener('touchstart', retryAutoplay, { passive: true });
    window.addEventListener('pointerdown', retryAutoplay, { passive: true });
    window.addEventListener('scroll', retryAutoplay, { passive: true });
    document.addEventListener('visibilitychange', retryAutoplay);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      window.removeEventListener('touchstart', retryAutoplay);
      window.removeEventListener('pointerdown', retryAutoplay);
      window.removeEventListener('scroll', retryAutoplay);
      document.removeEventListener('visibilitychange', retryAutoplay);
    };
  }, []);

  return (
    <div className="features-page">
      <Helmet>
        <title>Features | 22 Horse Racing Analysis Tables in One Racecard | HorseStat</title>
        <meta name="description" content="Explore HorseStat's full feature set: interactive racecards, form analysis, trainer and jockey stats, pace maps, draw bias, and our proprietary HorseStat Rating System." />
        <link rel="canonical" href={absoluteUrl('/features')} />
        <meta property="og:title" content="HorseStat features: 22 analysis tables in one racecard" />
        <meta property="og:description" content="Explore HorseStat's full feature set: interactive racecards, form analysis, trainer and jockey stats, pace maps, draw bias, and our proprietary HorseStat Rating System." />
        <meta property="og:url" content={absoluteUrl('/features')} />
        <meta property="og:image" content={`${absoluteUrl('/social/horsestat-card.png')}`} />
        <meta name="twitter:title" content="HorseStat features: 22 analysis tables in one racecard" />
        <meta name="twitter:description" content="Explore HorseStat's full feature set: interactive racecards, form analysis, trainer and jockey stats, pace maps, draw bias, and our proprietary HorseStat Rating System." />
      </Helmet>
      {/* Hero */}
      <section className="fs-hero">
        <div className="fs-hero-body">
          <h1 className="fs-hero-h1">
            22 angles.{' '}
            <span className="fs-hero-accent">Every horse. Every race.</span>
          </h1>
          <p className="fs-hero-sub">
            HorseStat is designed to help you make better betting decisions. See
            how every runner stacks up with complete, connected race data in one
            view.
          </p>
        </div>
        <div style={{ height: '3rem' }} />
        <div className="fs-hero-wave">
          <svg
            viewBox="0 0 1440 160"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
          >
            <path d="M0,0 C360,160 1080,160 1440,0" fill="#f5f3ff" />
          </svg>
        </div>
      </section>

      {/* Feature sections */}
      <div className="fs-sections">
        {featureSections.map((s) => (
          <FeatureSection key={s.id} section={s} />
        ))}
      </div>

      {/* CTA band */}
      <div className="fs-cta-wrap">
        <div className="fs-cta-wave">
          <svg
            viewBox="0 0 1440 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
          >
            <path
              d="M0,120 C360,0 1080,0 1440,120 L1440,121 L0,121 Z"
              fill="#611f69"
            />
          </svg>
        </div>
        <section className="fs-cta">
          <div className="fs-cta-inner">
            <h2>Built for serious analysis</h2>
            <p>
              22 data points per horse, every race, every day. The same depth
              of data, structured for clarity and speed.
            </p>
            <div className="fs-cta-btns">
              <a
                href="https://app.horsestat.com"
                className="fs-cta-primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Get Started Free
              </a>
              <Link to="/pricing" className="fs-cta-secondary">
                View Pricing
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default Features;
